/**
 * Utilitários de normalização de telefone brasileiro para WhatsApp
 * 
 * Formato esperado: 55DDDNÚMERO (e.g., 5511999999999)
 * - 55 = código do Brasil
 * - DDD = 2 dígitos (11-99)
 * - Número = 8 ou 9 dígitos (celular BR sempre 9 dígitos)
 */

/**
 * Normaliza qualquer input de telefone brasileiro para o formato internacional
 * Aceita formatos como:
 *   - (11) 99999-9999
 *   - 11999999999
 *   - +55 11 99999-9999
 *   - 5511999999999
 *   - etc.
 * 
 * @param input Número de telefone em qualquer formato
 * @returns String no formato "55DDDNÚMERO" ou null se inválido
 */
export function normalizeBrazilWhatsapp(input: string | null | undefined): string | null {
  if (!input) return null;

  // Remove tudo que não for dígito
  let digits = input.replace(/\D/g, '');

  // Se vazio após limpeza, inválido
  if (digits.length === 0) return null;

  // Remove prefixo 0 de ligação local (se houver)
  if (digits.startsWith('0') && digits.length > 10) {
    digits = digits.substring(1);
  }

  // Se já começa com 55, assume que está completo
  if (digits.startsWith('55')) {
    // 55 + DDD (2) + número (8 ou 9) = 12 ou 13 dígitos
    if (digits.length === 12) {
      // Celular antigo (8 dígitos) - adicionar 9 na frente
      const ddd = digits.substring(2, 4);
      const number = digits.substring(4);
      
      // DDDs de SP que podem ter 8 dígitos de celular antigo
      if (!number.startsWith('9') && isValidDDD(ddd)) {
        return `55${ddd}9${number}`;
      }
      return digits;
    }
    if (digits.length === 13) {
      return digits; // Já está no formato correto
    }
    // Outros tamanhos com prefixo 55 são inválidos
    return null;
  }

  // Se NÃO começa com 55
  // Formato: DDD (2) + número (8 ou 9) = 10 ou 11 dígitos
  if (digits.length === 10) {
    // Celular antigo (8 dígitos) - adicionar 9 na frente do número
    const ddd = digits.substring(0, 2);
    const number = digits.substring(2);
    
    if (!isValidDDD(ddd)) return null;
    
    // Adicionar 9 na frente e prefixo 55
    return `55${ddd}9${number}`;
  }
  
  if (digits.length === 11) {
    // Celular novo (9 dígitos)
    const ddd = digits.substring(0, 2);
    const number = digits.substring(2);
    
    if (!isValidDDD(ddd)) return null;
    
    // Validar que começa com 9 (celular)
    if (!number.startsWith('9')) {
      // Pode ser telefone fixo, não aceitar para WhatsApp
      return null;
    }
    
    return `55${ddd}${number}`;
  }

  // Qualquer outro formato é inválido
  return null;
}

/**
 * Converte número normalizado para JID do WhatsApp
 * 
 * @param normalizedNumber Número no formato 55DDDNÚMERO (sem formatação)
 * @returns String no formato "55DDDNÚMERO@s.whatsapp.net"
 */
export function toWhatsappJid(normalizedNumber: string): string {
  // Garantir que está limpo
  const clean = normalizedNumber.replace(/\D/g, '');
  return `${clean}@s.whatsapp.net`;
}

/**
 * Formata número normalizado para exibição
 * Ex: 5511999999999 -> +55 (11) 99999-9999
 */
export function formatWhatsappDisplay(normalizedNumber: string | null | undefined): string {
  if (!normalizedNumber) return '';
  
  const digits = normalizedNumber.replace(/\D/g, '');
  
  if (digits.length !== 13) return normalizedNumber;
  
  const country = digits.substring(0, 2);
  const ddd = digits.substring(2, 4);
  const firstPart = digits.substring(4, 9);
  const secondPart = digits.substring(9);
  
  return `+${country} (${ddd}) ${firstPart}-${secondPart}`;
}

/**
 * Valida se um DDD brasileiro é válido
 */
function isValidDDD(ddd: string): boolean {
  const validDDDs = [
    // São Paulo
    '11', '12', '13', '14', '15', '16', '17', '18', '19',
    // Rio de Janeiro
    '21', '22', '24',
    // Espírito Santo
    '27', '28',
    // Minas Gerais
    '31', '32', '33', '34', '35', '37', '38',
    // Paraná
    '41', '42', '43', '44', '45', '46',
    // Santa Catarina
    '47', '48', '49',
    // Rio Grande do Sul
    '51', '53', '54', '55',
    // Distrito Federal e Goiás
    '61', '62', '64',
    // Mato Grosso
    '65', '66',
    // Mato Grosso do Sul
    '67',
    // Acre
    '68',
    // Rondônia
    '69',
    // Bahia
    '71', '73', '74', '75', '77',
    // Sergipe
    '79',
    // Pernambuco
    '81', '87',
    // Alagoas
    '82',
    // Paraíba
    '83',
    // Rio Grande do Norte
    '84',
    // Ceará
    '85', '88',
    // Piauí
    '86', '89',
    // Maranhão
    '98', '99',
    // Pará
    '91', '93', '94',
    // Amapá
    '96',
    // Amazonas
    '92', '97',
    // Roraima
    '95',
    // Tocantins
    '63',
  ];

  return validDDDs.includes(ddd);
}

/**
 * Extrai número do JID do WhatsApp
 * Ex: "5511999999999@s.whatsapp.net" -> "5511999999999"
 */
export function extractNumberFromJid(jid: string | undefined | null): string | null {
  if (!jid) return null;
  
  // Formato: number@s.whatsapp.net
  const match = jid.match(/^(\d+)@/);
  return match ? match[1] : null;
}

/**
 * Valida se um número normalizado é válido
 */
export function isValidBrazilianWhatsapp(normalizedNumber: string | null | undefined): boolean {
  if (!normalizedNumber) return false;
  
  const digits = normalizedNumber.replace(/\D/g, '');
  
  // Deve ter 13 dígitos (55 + DDD + 9 dígitos)
  if (digits.length !== 13) return false;
  
  // Deve começar com 55
  if (!digits.startsWith('55')) return false;
  
  // DDD deve ser válido
  const ddd = digits.substring(2, 4);
  if (!isValidDDD(ddd)) return false;
  
  // Número deve começar com 9 (celular)
  const number = digits.substring(4);
  if (!number.startsWith('9')) return false;
  
  return true;
}
